'use client';

import { useSearchParams } from 'next/navigation';
import { Suspense } from 'react';
import { Icon } from '@/components/entities/Sprite';
import { cn } from '@/utils/cn';
import PaginationItem from './components/PaginationItem';

interface Props {
  pageNumber: number;
  totalItems: number;
  totalPages: number;
  onPageChangeAction?: (page: number) => void;
  variant?: 'button' | 'link';
  className?: string;
  getHrefAction?: (page: number) => string;
}

const noop = () => {};

function PaginationInner({
  pageNumber,
  totalPages,
  className,
  onPageChangeAction = noop,
  variant = 'button',
  getHrefAction,
}: Props) {
  const searchParams = useSearchParams();

  const buildHref = (page: number) => {
    if (getHrefAction) {
      return getHrefAction(page);
    }

    const params = new URLSearchParams(searchParams);
    params.set('page', String(page));
    return `?${params.toString()}`;
  };

  const handlePageClick = (page: number) => {
    onPageChangeAction(page);
  };

  const handlePrev = () => {
    if (pageNumber > 1) {
      handlePageClick(pageNumber - 1);
    }
  };

  const handleNext = () => {
    if (pageNumber < totalPages) {
      handlePageClick(pageNumber + 1);
    }
  };

  const getPageButtons = (pageNumber: number, totalPages: number): number[] => {
    if (totalPages <= 1) {
      return [];
    }

    // Up to 5 pages fit without truncation, so show them all.
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const maxVisible = 3;
    let start = Math.max(1, pageNumber - 1);
    const end = Math.min(totalPages, start + maxVisible - 1);

    start = Math.max(1, end - maxVisible + 1);

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };
  const pageButtons = getPageButtons(pageNumber, totalPages);
  const lastVisiblePage = pageButtons.at(-1) ?? 0;
  // Only show the ellipsis when at least one page is hidden between the window and the last page.
  const showEllipsis = lastVisiblePage < totalPages - 1;
  const showLastPage = lastVisiblePage < totalPages;

  return (
    <div className={cn('flex items-center justify-center gap-2 mt-8 max-md:mt-2.75 md:ml-3.5 md:justify-start', className)}>
      <PaginationItem
        variant={variant}
        href={buildHref(pageNumber - 1)}
        onClick={handlePrev}
        disabled={pageNumber === 1}
        ariaLabel="Previous page"
        className="flex items-center justify-center bg-main-800 size-10 rounded-full disabled:opacity-60 aria-disabled:opacity-60 hover:bg-main-800/60 disabled:hover:bg-main-800 aria-disabled:hover:bg-main-800 disabled:cursor-auto aria-disabled:cursor-auto"
      >
        <Icon name="arrow-pagination" className="text-white size-6" />
      </PaginationItem>

      {pageButtons.map(page => (
        <PaginationItem
          key={page}
          variant={variant}
          href={buildHref(page)}
          onClick={() => handlePageClick(page)}
          ariaCurrent={pageNumber === page ? 'page' : undefined}
          className={cn(
            'rounded-full size-10 paragraph-s-medium transition-colors flex items-center justify-center',
            {
              'bg-c400 text-white hover:bg-c400/60': pageNumber === page,
              'bg-main-800 text-c400 hover:bg-main-800/60': pageNumber !== page,
            },
          )}
        >
          {page}
        </PaginationItem>
      ))}

      {showEllipsis && (
        <span className="flex items-center justify-center bg-main-800 size-10 rounded-full text-c400">
          <Icon name="more-dots" className="text-c400 size-4" />
        </span>
      )}

      {showLastPage && (
        <PaginationItem
          variant={variant}
          href={buildHref(totalPages)}
          onClick={() => handlePageClick(totalPages)}
          ariaCurrent={pageNumber === totalPages ? 'page' : undefined}
          className={cn(
            'rounded-full size-10 paragraph-s-medium transition-colors flex items-center justify-center',
            {
              'bg-c400 text-white hover:bg-c400/60': pageNumber === totalPages,
              'bg-main-800 text-c400 hover:bg-main-800/60': pageNumber !== totalPages,
            },
          )}
        >
          {totalPages}
        </PaginationItem>
      )}

      <PaginationItem
        variant={variant}
        href={buildHref(pageNumber + 1)}
        onClick={handleNext}
        disabled={pageNumber === totalPages}
        ariaLabel="Next page"
        className="flex items-center justify-center bg-main-800 size-10 rounded-full disabled:opacity-60 aria-disabled:opacity-60 hover:bg-main-800/60 disabled:hover:bg-main-800 aria-disabled:hover:bg-main-800 disabled:cursor-auto aria-disabled:cursor-auto"
      >
        <Icon name="arrow-pagination" className="rotate-180 text-white size-6" />
      </PaginationItem>
    </div>
  );
}

export default function Pagination(props: Props) {
  return (
    <Suspense fallback={null}>
      <PaginationInner {...props} />
    </Suspense>
  );
}
